// Taking a chart away with you: the numbers as CSV, the picture as PNG or a
// one-page PDF.
//
// The CSV half is pure (spec → text), so it is tested in the node-only runner
// alongside the rest of chartSpec. The PNG half needs the live canvas, so it
// takes a Chart.js instance — and paints the surface token behind it first,
// because a canvas is transparent and a dark-theme chart dropped into a light
// document would otherwise be an unreadable smear of dark text on white.

import type { Chart as ChartJS } from 'chart.js'

import type { ChartSpec } from './chartSpec'

/** A cell that can't corrupt the row it sits in. */
function cell(value: string | number | null | undefined): string {
  if (value == null) return ''
  const text = String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function isoOf(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toISOString()
}

/** Half the tightest sampling interval in the chart, as the window within which
 *  two different series count as "the same moment".
 *
 *  Zabbix polls each item on its own schedule, so a host's CPU and memory land
 *  seconds apart all day. Aligning on exact timestamps would give one row per
 *  reading with every other column blank — 240 half-empty rows for a 24h,
 *  two-metric chart. Grouping never touches a value and never merges two
 *  readings of the SAME series; it only decides which rows sit together.
 */
function alignmentWindow(spec: ChartSpec): number {
  const gaps: number[] = []
  for (const s of spec.series) {
    const deltas = s.points.slice(1).map((p, i) => p.x - s.points[i].x).filter((d) => d > 0)
    if (deltas.length) {
      deltas.sort((a, b) => a - b)
      gaps.push(deltas[Math.floor(deltas.length / 2)])
    }
  }
  return gaps.length ? Math.min(...gaps) / 2 : 0
}

/** The chart's data as CSV: one row per sample moment, one column per series.
 *
 *  Series are aligned on their x values rather than zipped by index — a chart
 *  can hold a measured line and a projection that start at different points,
 *  and zipping those would pair a May reading with a June forecast. Readings
 *  that don't line up stay in their own row with the other columns empty.
 */
export function toCsv(spec: ChartSpec): string {
  const labels = spec.series.map((s) => (s.unit ? `${s.label} (${s.unit})` : s.label))

  if (spec.categorical) {
    const rows = spec.labels.map((name, i) => [
      cell(name),
      ...spec.series.map((s) => cell(s.data[i] ?? null)),
    ].join(','))
    return [['category', ...labels.map(cell)].join(','), ...rows].join('\n')
  }

  const window = alignmentWindow(spec)
  const readings = spec.series
    .flatMap((s, index) => s.points.map((p) => ({ x: p.x, y: p.y, index })))
    .sort((a, b) => a.x - b.x)

  const rows: { x: number; values: (number | null)[] }[] = []
  for (const reading of readings) {
    const open = rows[rows.length - 1]
    const fits = open
      && reading.x - open.x <= window
      && open.values[reading.index] === null
    if (fits) {
      open.values[reading.index] = reading.y
    } else {
      const values = spec.series.map(() => null) as (number | null)[]
      values[reading.index] = reading.y
      rows.push({ x: reading.x, values })
    }
  }

  const xHeader = spec.timed ? 'time' : spec.xLabel || 'x'
  const body = rows.map((row) => [
    cell(spec.timed ? isoOf(row.x) : row.x),
    ...row.values.map(cell),
  ].join(','))

  return [[xHeader, ...labels.map(cell)].join(','), ...body].join('\n')
}

/** A filesystem-safe stem from the chart's own title. */
export function exportFilename(spec: ChartSpec, extension: 'csv' | 'png' | 'pdf', now = new Date()): string {
  const stem = (spec.title || 'chart')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'chart'
  const day = now.toISOString().slice(0, 10)
  return `${stem}-${day}.${extension}`
}

/** Hand the browser a file. Works inside the widget's shadow DOM: the anchor
 *  is created in the light document, clicked, and dropped again. */
export function downloadFile(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Give the click a tick to start before the URL stops resolving.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function downloadCsv(spec: ChartSpec): void {
  // The BOM is what makes Excel read UTF-8 instead of mangling the µ in µs
  // and the drive labels.
  const blob = new Blob([`﻿${toCsv(spec)}`], { type: 'text/csv;charset=utf-8' })
  downloadFile(exportFilename(spec, 'csv'), blob)
}

/** The chart as a PNG, on an opaque background so it survives being pasted
 *  into a ticket, an email or a document. */
export function chartToPngBlob(chart: ChartJS, background: string): Promise<Blob | null> {
  const source = chart.canvas
  const out = document.createElement('canvas')
  out.width = source.width
  out.height = source.height
  const ctx = out.getContext('2d')
  if (!ctx) return Promise.resolve(null)
  ctx.fillStyle = background
  ctx.fillRect(0, 0, out.width, out.height)
  ctx.drawImage(source, 0, 0)
  return new Promise((resolve) => out.toBlob((blob) => resolve(blob), 'image/png'))
}

export async function downloadPng(chart: ChartJS, spec: ChartSpec, background: string): Promise<void> {
  const blob = await chartToPngBlob(chart, background)
  if (blob) downloadFile(exportFilename(spec, 'png'), blob)
}

/** A one-page PDF whose page IS the image (1 px = 1 pt), hand-assembled —
 *  embedding a JPEG under /DCTDecode needs no compression code and no
 *  dependency, and a chart snapshot has no transparency to lose. Pure
 *  (bytes in, bytes out) so the node runner can pin the structure. */
export function jpegToPdfBytes(jpeg: Uint8Array, width: number, height: number): Uint8Array {
  const encoder = new TextEncoder()
  const contents = `q\n${width} 0 0 ${height} 0 0 cm\n/Im0 Do\nQ\n`
  const head = [
    '',  // object numbers are 1-based
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${width} ${height}] ` +
      '/Contents 4 0 R /Resources << /XObject << /Im0 5 0 R >> >> >>',
    `<< /Length ${contents.length} >>\nstream\n${contents}endstream`,
  ]
  const imageDict =
    `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} ` +
    `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`

  const parts: Uint8Array[] = []
  const offsets: number[] = []
  let position = 0
  const push = (chunk: Uint8Array) => { parts.push(chunk); position += chunk.length }
  const pushText = (text: string) => push(encoder.encode(text))

  pushText('%PDF-1.4\n')
  for (let n = 1; n <= 4; n++) {
    offsets[n] = position
    pushText(`${n} 0 obj\n${head[n]}\nendobj\n`)
  }
  offsets[5] = position
  pushText(`5 0 obj\n${imageDict}`)
  push(jpeg)
  pushText('\nendstream\nendobj\n')

  const xref = position
  pushText(
    'xref\n0 6\n0000000000 65535 f \n' +
    [1, 2, 3, 4, 5].map((n) => `${String(offsets[n]).padStart(10, '0')} 00000 n \n`).join('') +
    `trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`,
  )

  const out = new Uint8Array(position)
  let cursor = 0
  for (const chunk of parts) { out.set(chunk, cursor); cursor += chunk.length }
  return out
}

/** The chart as a one-page PDF, painted on the surface colour like the PNG. */
export function chartToPdfBlob(chart: ChartJS, background: string): Blob | null {
  const source = chart.canvas
  const out = document.createElement('canvas')
  out.width = source.width
  out.height = source.height
  const ctx = out.getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = background
  ctx.fillRect(0, 0, out.width, out.height)
  ctx.drawImage(source, 0, 0)
  const dataUrl = out.toDataURL('image/jpeg', 0.95)
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1)
  const binary = atob(base64)
  const jpeg = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) jpeg[i] = binary.charCodeAt(i)
  const bytes = jpegToPdfBytes(jpeg, out.width, out.height)
  return new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' })
}

export function downloadPdf(chart: ChartJS, spec: ChartSpec, background: string): void {
  const blob = chartToPdfBlob(chart, background)
  if (blob) downloadFile(exportFilename(spec, 'pdf'), blob)
}
